import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { UserManager, User, WebStorageStateStore } from 'oidc-client-ts';
import { config } from './config';
import { setIdToken, ensureIdentityId } from './credentials';

// Cognito's Hosted UI domain doesn't serve `.well-known/openid-configuration` (404, and no CORS
// even if it did), so oidc-client-ts's default discovery fetch fails with an opaque
// "TypeError: Failed to fetch". Provide the metadata explicitly to skip discovery entirely.
// `authority` must be the User Pool's issuer (matches the ID token's `iss` claim) — it is NOT the
// same as the Hosted UI domain, which only hosts the OAuth2 authorize/token/logout endpoints.
const hostedUiDomain = `https://${config.userPoolDomain}`;
const issuer = `https://cognito-idp.${config.awsRegion}.amazonaws.com/${config.userPoolId}`;

const userManager = new UserManager({
  authority: issuer,
  metadata: {
    issuer,
    authorization_endpoint: `${hostedUiDomain}/oauth2/authorize`,
    token_endpoint: `${hostedUiDomain}/oauth2/token`,
    userinfo_endpoint: `${hostedUiDomain}/oauth2/userInfo`,
    end_session_endpoint: `${hostedUiDomain}/logout`,
    jwks_uri: `${issuer}/.well-known/jwks.json`,
  },
  client_id: config.userPoolClientId,
  redirect_uri: `${window.location.origin}/sign-in/callback`,
  post_logout_redirect_uri: `${window.location.origin}/`,
  response_type: 'code',
  scope: 'openid email profile',
  loadUserInfo: false,
  automaticSilentRenew: true,
  // Keep tokens in sessionStorage so they survive a reload within a tab but not a browser restart.
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
});

interface AuthState {
  user: User | null;
  identityId: string | null;
  loading: boolean;
  error: string | null;
  signIn: () => void;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [identityId, setIdentityId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const u = await userManager.getUser();
        if (mounted && u && !u.expired) {
          await applyUser(u, setUser, setIdentityId);
        }
      } catch (e) {
        if (mounted) setError(String(e));
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    const onLoaded = (u: User) => applyUser(u, setUser, setIdentityId).catch((e) => setError(String(e)));
    const onUnloaded = () => {
      setUser(null);
      setIdentityId(null);
      setIdToken(null);
    };
    const onSilentRenewError = async () => {
      // Token couldn't be refreshed silently — sign the user out and let them sign in again.
      await userManager.removeUser();
    };
    userManager.events.addUserLoaded(onLoaded);
    userManager.events.addUserUnloaded(onUnloaded);
    userManager.events.addSilentRenewError(onSilentRenewError);

    return () => {
      mounted = false;
      userManager.events.removeUserLoaded(onLoaded);
      userManager.events.removeUserUnloaded(onUnloaded);
      userManager.events.removeSilentRenewError(onSilentRenewError);
    };
  }, []);

  const signIn = () => {
    userManager.signinRedirect().catch((e) => setError(String(e)));
  };
  const signOut = async () => {
    try {
      await userManager.signoutRedirect();
    } catch (e) {
      setError(String(e));
    }
  };

  return (
    <AuthContext.Provider value={{ user, identityId, loading, error, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

async function applyUser(
  u: User,
  setUser: (u: User) => void,
  setIdentityId: (id: string) => void,
): Promise<void> {
  if (!u.id_token) throw new Error('No ID token in session.');
  setIdToken(u.id_token);
  const id = await ensureIdentityId();
  setUser(u);
  setIdentityId(id);
}

export function useAuth(): AuthState {
  const v = useContext(AuthContext);
  if (!v) throw new Error('useAuth must be used inside AuthProvider');
  return v;
}

export function SignInPage() {
  const { signIn, error } = useAuth();
  useEffect(() => {
    signIn();
  }, [signIn]);
  return (
    <div className="app-shell">
      <h1>VOD Management</h1>
      {error ? <p className="error">Sign-in failed: {error}</p> : <p>Signing you in…</p>}
    </div>
  );
}

export function SignInCallbackPage() {
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    userManager
      .signinCallback()
      .then(() => {
        window.location.replace('/library');
      })
      .catch((e) => setError(String(e)));
  }, []);
  return (
    <div className="app-shell">
      <h1>VOD Management</h1>
      {error ? (
        <p className="error">Sign-in failed: {error}</p>
      ) : (
        <p>Completing sign-in…</p>
      )}
    </div>
  );
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="app-shell">
        <p className="muted">Loading…</p>
      </div>
    );
  }
  if (!user || user.expired) {
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }
  return <>{children}</>;
}
